import { z } from "zod";

/**
 * Wire contracts (Zod schemas + inferred types + the `userReputationPath`
 * builder colocated with its schema) for the public-workspace surface.
 *
 * Split out of `public-workspaces.ts` to keep each file under the 400-LOC
 * ceiling. `public-workspaces.ts` re-exports everything here, so the public
 * import surface (`@ano/shared/api/public-workspaces`) is unchanged.
 */

export const publicWorkspaceMetadataSchema = z.object({
  workspace_id: z.string(),
  name: z.string(),
  slug: z.string(),
  logo_url: z.string().nullable(),
  public_join_policy: z.enum(["auto", "request"]),
  // Community config surfaced to non-members on the splash (Phase 0).
  // Optional/nullable so the contract stays backward-compatible with
  // workspaces that have no community_settings.
  tagline: z.string().nullable().optional(),
  rules: z.string().nullable().optional(),
  /** Owner-curated rooms, server-redacted to PUBLIC channels only. */
  featured_channels: z
    .array(z.object({ id: z.string(), name: z.string() }))
    .optional(),
  /** Whether the splash should offer read-only preview (Phase 1). */
  entry_mode: z.enum(["preview_then_join", "join_first"]).optional(),
  /** Social proof for the landing page. */
  member_count: z.number().optional(),
  channel_count: z.number().optional(),
  /**
   * Phase 5 — logged-out visitors may read the featured digest without
   * signing in. Drives the anonymous (no-auth) landing surface.
   */
  anonymous_view: z.boolean().optional(),
  /**
   * Phase 5 — search engines may index the public content. Drives the edge
   * middleware's crawler-content + robots/sitemap. Implies anonymous_view.
   */
  search_indexing: z.boolean().optional(),
});

export type PublicWorkspaceMetadata = z.infer<
  typeof publicWorkspaceMetadataSchema
>;

// ── Read-only preview (Phase 1) ────────────────────────────────────────────
// A server-REDACTED digest of FEATURED channels a non-member can read before
// joining (only when entry_mode === 'preview_then_join'). Each message is the
// denormalized sender name + the kind-agnostic `preview_text` + timestamp —
// no email, no private channels, top-level only.

export const communityPreviewMessageSchema = z.object({
  id: z.string(),
  sender_name: z.string().nullable(),
  text: z.string(),
  created_at: z.number(),
});

export const communityPreviewChannelSchema = z.object({
  id: z.string(),
  name: z.string(),
  topic: z.string().nullable().optional(),
  messages: z.array(communityPreviewMessageSchema),
});

export const communityPreviewSchema = z.object({
  entry_mode: z.enum(["preview_then_join", "join_first"]),
  channels: z.array(communityPreviewChannelSchema),
});
export type CommunityPreview = z.infer<typeof communityPreviewSchema>;
export type CommunityPreviewChannel = z.infer<
  typeof communityPreviewChannelSchema
>;

// ── Public channel index + per-channel content (Phase 5 SEO) ────────────────
// When the owner enabled `anonymous_view`, EVERY public (non-private, non-
// archived) channel becomes an indexable, no-login surface — so a search like
// "whats the best database" can land on `whats-the-best.ano.dev/c/database`.
// Same redaction as the preview: denormalized sender name + preview_text, top-
// level only, never private channels / DMs / emails.

export const publicChannelSummarySchema = z.object({
  name: z.string(),
  topic: z.string().nullable().optional(),
  message_count: z.number().optional(),
  last_activity_at: z.number().nullable().optional(),
});
export type PublicChannelSummary = z.infer<typeof publicChannelSummarySchema>;

export const publicChannelsResponseSchema = z.object({
  workspace_name: z.string(),
  channels: z.array(publicChannelSummarySchema),
});
export type PublicChannelsResponse = z.infer<
  typeof publicChannelsResponseSchema
>;

export const publicChannelContentSchema = z.object({
  workspace_name: z.string(),
  channel: z.object({
    // The channel_id — a logged-out visitor needs it to post a top-level reply
    // after one-click signup (the reply targets this channel, not a thread).
    id: z.string(),
    name: z.string(),
    topic: z.string().nullable().optional(),
  }),
  messages: z.array(communityPreviewMessageSchema),
});
export type PublicChannelContent = z.infer<typeof publicChannelContentSchema>;

// ── Single shared question (anchor deep link) ───────────────────────────────
// A logged-out visitor landing on `…/c/<channel>/m/<messageId>` reads ONE
// question + its answers (server-redacted, same exposure as the channel
// content). The question reuses the preview message shape, extended with the
// Q&A metadata (thread title + which answer the asker accepted); each answer is
// a plain preview message. Drives the OG share card and the anchored reply box.

export const publicQuestionResponseSchema = z.object({
  workspace_name: z.string(),
  channel: z.object({
    id: z.string(),
    name: z.string(),
    topic: z.string().nullable().optional(),
  }),
  question: communityPreviewMessageSchema.extend({
    thread_title: z.string().nullable().optional(),
    accepted_answer_id: z.string().nullable().optional(),
  }),
  answers: z.array(communityPreviewMessageSchema),
});
export type PublicQuestionResponse = z.infer<
  typeof publicQuestionResponseSchema
>;

// ── Top contributors (Phase 3 recognition) ─────────────────────────────────
// Derived from community_signals (NOT a stored counter): members whose
// messages in PUBLIC channels received the most curation marks. Surfaced on
// the landing so newcomers see who's helpful. Public display name + avatar
// only (same exposure as the message previews); never email.

export const publicContributorSchema = z.object({
  user_id: z.string(),
  name: z.string().nullable(),
  avatar_url: z.string().nullable().optional(),
  signal_count: z.number(),
});
export type PublicContributor = z.infer<typeof publicContributorSchema>;

export const publicContributorsResponseSchema = z.object({
  workspace_name: z.string(),
  contributors: z.array(publicContributorSchema),
});
export type PublicContributorsResponse = z.infer<
  typeof publicContributorsResponseSchema
>;

// ── Portable cross-community reputation ────────────────────────────────────
// A user's signal counts aggregated across EVERY public community (not one
// workspace) — the portable status that makes recognition a network effect.
export const userReputationSchema = z.object({
  total: z.number(),
  by_type: z.object({
    helpful: z.number(),
    worth_reading: z.number(),
    best_answer: z.number(),
  }),
});
export type UserReputation = z.infer<typeof userReputationSchema>;

export function userReputationPath(userId: string): string {
  return `/api/users/${encodeURIComponent(userId)}/reputation`;
}

// ── Rich public profile card ───────────────────────────────────────────────
// Server-redacted public profile shown when a username is clicked in a public
// community: identity + profile-metadata extras + live GitHub + reputation.
export const githubRepoSchema = z.object({
  name: z.string(),
  description: z.string().nullable(),
  stars: z.number(),
  url: z.string(),
});
export const githubProfileSchema = z.object({
  login: z.string(),
  name: z.string().nullable(),
  avatar_url: z.string().nullable(),
  bio: z.string().nullable(),
  html_url: z.string(),
  public_repos: z.number(),
  followers: z.number(),
  top_repos: z.array(githubRepoSchema),
});
export type GithubProfile = z.infer<typeof githubProfileSchema>;

export const userProfileSchema = z.object({
  user_id: z.string(),
  display_name: z.string().nullable(),
  avatar_url: z.string().nullable(),
  headline: z.string().nullable(),
  bio: z.string().nullable(),
  location: z.string().nullable(),
  website_url: z.string().nullable(),
  building_now: z.string().nullable(),
  // Read side is permissive — the platform/url were validated on write.
  social_links: z.array(z.object({ platform: z.string(), url: z.string() })),
  github_username: z.string().nullable(),
  github: githubProfileSchema.nullable(),
  reputation: userReputationSchema,
});
export type UserProfile = z.infer<typeof userProfileSchema>;

export function userProfilePath(userId: string): string {
  return `/api/users/${encodeURIComponent(userId)}/profile`;
}

export const autoJoinResponseSchema = z.object({
  workspace_id: z.string(),
});
export type AutoJoinResponse = z.infer<typeof autoJoinResponseSchema>;

export const requestJoinResponseSchema = z.object({
  join_request_id: z.string(),
  status: z.enum(["pending", "approved", "rejected", "cancelled"]),
});
export type RequestJoinResponse = z.infer<typeof requestJoinResponseSchema>;

export const requestJoinBodySchema = z.object({
  message: z.string().max(500).optional(),
});
export type RequestJoinBody = z.infer<typeof requestJoinBodySchema>;
